/**
 * MoveFolderModal — Salesforce-style folder picker for reports + dashboards.
 *
 * Two consumers, one component:
 *   - Reports list  → folders are objects `{id, name, color}`
 *   - Dashboards list → folders are flat strings ("Sales", "Executive")
 *
 * The caller passes a normalised `folders` array (caller knows which
 * shape it has) and a `currentFolder` (string for both, since reports
 * store folder by name on the doc and dashboards do the same).
 * `onMove(folderName)` is the single hook — the caller decides whether
 * to call `useReportMutations.moveToFolder` or
 * `useDashboardMutations.moveToFolder`.
 *
 * Empty / unfiled is a real choice — picking the "(Unfiled)" row sends
 * the literal "Unfiled" string, which both backends route through their
 * cascade-to-default logic.
 */
import React, { useMemo, useState } from 'react';
import {
  Folder, FolderOpen, Inbox, Search, X, Check, Loader2,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const UNFILED_KEY = '__unfiled__';
const UNFILED_LABEL = 'Unfiled';

const MoveFolderModal = ({
  open,
  onClose,
  // The full folder catalog. Strings ("Sales") or objects ({id, name}).
  // Mixed types are normalised below.
  folders = [],
  currentFolder,
  // Asset name for the header — "Move 'Sales Q3 Report' to…".
  itemName,
  // (folderName: string) => Promise<any>
  onMove,
  // Title override — "Move Report" or "Move Dashboard". Falls back to a
  // generic label so the modal still reads cleanly without it.
  kindLabel = 'Item',
}) => {
  const [search, setSearch] = useState('');
  const [moving, setMoving] = useState(null);  // folder name in flight
  const [error, setError] = useState(null);

  // Normalise the folder list into a uniform `{key, name, count?}` shape
  // so the render loop below doesn't branch on the source structure.
  // Prepend the "Unfiled" pseudo-folder so "move to Unfiled" works the
  // same way as moving to any other folder.
  const normalised = useMemo(() => {
    const rows = [{ key: UNFILED_KEY, name: UNFILED_LABEL, isUnfiled: true }];
    for (const f of folders) {
      if (typeof f === 'string') {
        if (f && f !== UNFILED_LABEL) rows.push({ key: f, name: f });
      } else if (f && typeof f === 'object') {
        if (f.name && f.name !== UNFILED_LABEL) {
          rows.push({ key: f.id || f.name, name: f.name, color: f.color });
        }
      }
    }
    return rows;
  }, [folders]);

  const filtered = useMemo(() => {
    if (!search.trim()) return normalised;
    const q = search.trim().toLowerCase();
    return normalised.filter((r) => r.name.toLowerCase().includes(q));
  }, [normalised, search]);

  const handlePick = async (row) => {
    const targetName = row.isUnfiled ? UNFILED_LABEL : row.name;
    if (moving) return;  // dedupe rapid clicks
    if (targetName === (currentFolder || UNFILED_LABEL)) {
      onClose();
      return;
    }
    setMoving(targetName);
    setError(null);
    try {
      await onMove(targetName);
      onClose();
    } catch (e) {
      setError(e?.message || `Could not move to "${targetName}"`);
    } finally {
      setMoving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !moving && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0">
        <DialogHeader className="px-6 py-4 border-b border-slate-200">
          <DialogTitle className="text-lg flex items-center gap-2 truncate">
            Move {kindLabel}
            {itemName && (
              <span className="font-normal text-slate-500 truncate">
                "{itemName}"
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Pick a destination folder. Counts update immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search folders…"
              className="pl-8 h-9"
              autoFocus
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="px-3 pb-3 max-h-72 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="text-center text-xs text-slate-400 py-6">
              No matching folders.
            </div>
          )}
          {filtered.map((row) => {
            const isCurrent = (currentFolder || UNFILED_LABEL) === row.name;
            const Icon = row.isUnfiled ? Inbox : (isCurrent ? FolderOpen : Folder);
            const busy = moving === row.name;
            return (
              <button
                key={row.key}
                type="button"
                disabled={!!moving}
                onClick={() => handlePick(row)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                  isCurrent
                    ? 'bg-indigo-50 text-indigo-900'
                    : 'hover:bg-slate-50 text-slate-700'
                } ${moving ? 'opacity-60' : 'cursor-pointer'}`}
              >
                <Icon className={`h-4 w-4 flex-shrink-0 ${
                  row.isUnfiled ? 'text-slate-400' : 'text-indigo-500'
                }`} />
                <span className="text-sm flex-1 truncate">{row.name}</span>
                {isCurrent && (
                  <span className="text-[10px] uppercase font-semibold text-indigo-600 bg-indigo-100 px-1.5 py-0.5 rounded">
                    Current
                  </span>
                )}
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
                {!busy && !isCurrent && (
                  <Check className="h-3.5 w-3.5 text-slate-300 opacity-0 group-hover:opacity-100" />
                )}
              </button>
            );
          })}
        </div>

        {error && (
          <div className="mx-6 mb-3 flex items-start gap-2 p-2.5 rounded-lg bg-rose-50 border border-rose-200">
            <span className="text-xs text-rose-700 flex-1">{error}</span>
            <button onClick={() => setError(null)} className="text-rose-400 hover:text-rose-600">
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose} disabled={!!moving}>
            {moving ? 'Moving…' : 'Cancel'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MoveFolderModal;
